import React, { ReactNode } from 'react';
import { getPlatformStyles, isDesktop } from '@direct-frontend/platform-code';
import { genericMemo } from '@direct-frontend/stdlib';

import { cn } from '../../lib/css/className/className';
import { TestProps } from '../../internal/types/testTypes';
import { useStack } from '../../hooks/useStack';
import { getSizeSkin } from '../../lib/skin/getSizeSkin/getSizeSkin';
import { getColorSkin } from '../../lib/skin/getColorSkin/getColorSkin';
import { Card } from '../Card';
import { TextAlign, textAlignToStack, TextGenericWithSkin } from '../TextGeneric/TextGenericWithSkin';
import { HStack } from '../Stack';

import './PageFooter.css';

const cls = cn('PageFooter');

getPlatformStyles({
    common: import('./PageFooter.css'),
    mobile: import('./PageFooter@mobile.css'),
    desktop: import('./PageFooter@desktop.css'),
});

/** Элемент подвала */
export type PageFooterItem = {
    /** Ключ элемента */
    key: string;
    /** Содержимое элемента */
    content: ReactNode;
    /** Внешний атрибут className */
    className?: string;
};

export interface PageFooterProps extends TestProps {
    /** Главные элементы подвала */
    mainItems: PageFooterItem[];

    /** Дополнительные элементы подвала */
    additionalItems?: PageFooterItem[];

    /**
     * Добавляет верхний разделитель
     * @default false
     */
    withSeparator?: boolean;

    /** Внешний атрибут className */
    className?: string;
}

type PageFooterItemsProps = {
    items?: PageFooterItem[];
    dataTestId?: string;
    textAlign?: TextAlign;
};

const PageFooterItems = genericMemo(function PageFooterItems({
    items,
    dataTestId = 'PageFooterItems',
    textAlign: rawTextAlign = 'left',
}: PageFooterItemsProps) {
    const textAlign = isDesktop() ? rawTextAlign : 'center';

    if (!items) {
        return null;
    }

    return (
        <HStack
            className={cls('items')}
            alignItems="flex-start"
            flexWrap="wrap"
            justifyContent={textAlignToStack[textAlign]}
        >
            {items.map(({ key, content, className }) => (
                <TextGenericWithSkin
                    textSize="current-caption"
                    skinSize="m"
                    color="current"
                    role="supplementary"
                    key={key}
                    data-testid={`${dataTestId}.${key}`}
                    className={className}
                    textAlign={textAlign}
                >
                    {content}
                </TextGenericWithSkin>
            ))}
        </HStack>
    );
});

const DESKTOP_STACK_PROPS = {
    type: 'horizontal',
    gap: 'current-geometry',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
} as const;

const MOBILE_STACK_PROPS = {
    type: 'vertical',
    gap: '6',
    justifyContent: 'flex-start',
    alignItems: 'center',
} as const;

/**
 * Компонент для отображения подвала страницы
 * @kind component
 */
export const PageFooter = genericMemo(function PageFooter({
    mainItems,
    additionalItems,
    withSeparator = false,
    className,
    'data-testid': dataTestId = 'PageFooter',
}: PageFooterProps) {
    const stackClasses = useStack(isDesktop() ? DESKTOP_STACK_PROPS : MOBILE_STACK_PROPS);

    const colorSkinClass = getColorSkin('gray-transparent-contour', {});
    const sizeSkinClass = getSizeSkin('m');

    return (
        <Card
            className={cls({ withSeparator }, [stackClasses, colorSkinClass, sizeSkinClass, className])}
            data-testid={dataTestId}
        >
            <PageFooterItems items={mainItems} data-testid={`${dataTestId}.MainItems`} />
            <PageFooterItems items={additionalItems} data-testid={`${dataTestId}.AdditionalItems`} textAlign="right" />
        </Card>
    );
});
